import { useEffect, useState } from 'react';
import ActivityKeywordTable from './ActivityKeywordTable';
import './searchKeywords.css';
import Pagination from '../../components/pagination/Pagination';
import TripKeywordTable from './TripKeywordTable';
import { supAddSearchKeywords, supDelSearchKeywords, supSearchKeywords } from '../../api/supportAdmin';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Dialog from '../../components/dialogModal/dialogModal';

const SearchKeywords = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [keywords, setKeywords] = useState([]);

    const [rowClickId, setRowClickId] = useState('');
    const [deleteModal, setDeleteModal] = useState(false);

    // Add Keyword Modal
    const [addKeywordModal, setAddKeywordModal] = useState(false);

    const [formData, setFormData] = useState({
        keyword: '',
        types: []
    });

    const loading = false;
    const [totalData, setTotalData] = useState(0);

    const limit = 10;

    const selectedType = searchParams.get('type') || 'trip';
    const pageNumber = Number(searchParams.get('page')) || 1;
    const offset = (pageNumber - 1) * limit;

    const keywordTypes = ['title', 'tag', 'organizer'];

    const capitalizeWords = (text = '') =>
        text
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

    const getSearchKeywords = async () => {
        try {
            const { data } = await supSearchKeywords({
                type: selectedType,
                search,
                status,
                offset,
                limit
            });

            setKeywords(data.response);
            setTotalData(data.totalCount);
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        getSearchKeywords();
    }, [selectedType, search, status, offset]);

    const paginationClick = (number) => {
        setSearchParams({
            type: selectedType,
            page: number
        });

        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const handleDelete = async () => {
        try {
            await supDelSearchKeywords(rowClickId, {
                type: selectedType
            });

            toast.success('Keyword deleted successfully.');

            setDeleteModal(false);
            setRowClickId('');

            getSearchKeywords();
        } catch (error) {
            console.log(error);
        }
    };

    const handleKeywordChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            keyword: e.target.value
        }));
    };

    const handleTypeChange = (type) => {
        setFormData((prev) => ({
            ...prev,
            types: prev.types.includes(type) ? prev.types.filter((item) => item !== type) : [...prev.types, type]
        }));
    };

    const handleAddKeyword = async () => {
        try {
            if (!formData.keyword.trim()) {
                return toast.error('Please enter keyword.');
            }

            if (!formData.types.length) {
                return toast.error('Please select at least one type.');
            }

            await supAddSearchKeywords(formData);

            toast.success('Keyword added successfully.');

            setFormData({
                keyword: '',
                types: []
            });

            setAddKeywordModal(false);

            getSearchKeywords();
        } catch (error) {
            console.log(error);
        }
    };

    return (
        <div className='searchKeywordPage'>
            <div className='searchKeywordHeader'>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <h2>Search Keywords</h2>

                    {selectedType === 'trip' && (
                        <button className='primaryBtn' onClick={() => setAddKeywordModal(true)}>
                            + Add Keyword
                        </button>
                    )}
                </div>

                <div className='orgBookingFilters'>
                    <select
                        value={selectedType}
                        className='select'
                        onChange={(e) =>
                            setSearchParams({
                                type: e.target.value,
                                page: 1
                            })
                        }
                    >
                        <option value='trip'>Trip</option>
                        <option value='activity'>Activity</option>
                    </select>

                    {selectedType === 'trip' && (
                        <select value={status} className='select' onChange={(e) => setStatus(e.target.value)}>
                            <option value=''>All Status</option>
                            <option value='true'>Active</option>
                            <option value='false'>Inactive</option>
                        </select>
                    )}

                    <input
                        type='text'
                        placeholder='Search keyword...'
                        value={search}
                        className='select'
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            {selectedType === 'trip' && (
                <TripKeywordTable
                    keywords={keywords}
                    offset={offset}
                    setRowClickId={setRowClickId}
                    setDeleteModal={setDeleteModal}
                />
            )}

            {selectedType === 'activity' && (
                <ActivityKeywordTable
                    keywords={keywords}
                    offset={offset}
                    setRowClickId={setRowClickId}
                    setDeleteModal={setDeleteModal}
                />
            )}

            <Pagination
                paginationClick={paginationClick}
                numberOfRows={totalData}
                offSet={offset}
                pageNumber={pageNumber}
                limit={limit}
            />

            {/* Delete Modal */}

            {deleteModal && (
                <Dialog openModal={deleteModal} heading='Delete Confirmation!'>
                    <div className='dialogContent'>
                        <p className='confirmationText'>Are you sure?</p>

                        <p className='confirmationMessage'>You want to delete this keyword.</p>

                        <div className='alignCenter'>
                            <button className='dialogActBtn submit' onClick={handleDelete}>
                                Yes
                            </button>

                            <button
                                className='dialogActBtn cancel'
                                onClick={() => {
                                    setDeleteModal(false);
                                    setRowClickId('');
                                }}
                            >
                                No
                            </button>
                        </div>
                    </div>
                </Dialog>
            )}

            {/* Add Keyword Modal */}

            {addKeywordModal && (
                <Dialog openModal={addKeywordModal} heading='Add Search Keyword'>
                    <div className='addKeywordModal'>
                        <label>Keyword</label>

                        <input
                            type='text'
                            className='addKeywordInput'
                            placeholder='Enter keyword'
                            value={formData.keyword}
                            onChange={handleKeywordChange}
                        />

                        <label>Select Types</label>

                        <div className='keywordTypeGrid'>
                            {keywordTypes.map((type) => (
                                <label key={type} className='keywordTypeOption'>
                                    <input
                                        type='checkbox'
                                        checked={formData.types.includes(type)}
                                        onChange={() => handleTypeChange(type)}
                                    />

                                    {capitalizeWords(type)}
                                </label>
                            ))}
                        </div>

                        <div className='addKeywordBtns'>
                            <button
                                className='dialogActBtn cancel'
                                onClick={() => {
                                    setAddKeywordModal(false);

                                    setFormData({
                                        keyword: '',
                                        types: []
                                    });
                                }}
                            >
                                Cancel
                            </button>

                            <button className='dialogActBtn submit' onClick={handleAddKeyword}>
                                Save
                            </button>
                        </div>
                    </div>
                </Dialog>
            )}
        </div>
    );
};

export default SearchKeywords;
